/**
 * Central brand configuration.
 *
 * Every user-visible occurrence of the brand name, marketing/app URL, help
 * centre link, support address and social handle should come from here rather
 * than being hardcoded at the call site. Rebranding then means changing env
 * vars (or the defaults below) instead of editing ~200 files.
 *
 * NOTE: Create React App inlines `process.env.REACT_APP_*` at BUILD time, and
 * only when referenced as a full literal (`process.env.REACT_APP_FOO`) — it
 * cannot be indexed dynamically. Changing any of these requires a rebuild and
 * redeploy, not just a process restart.
 *
 * Defaults are the live production values, because deployments do NOT set these
 * env vars. The env vars exist as an override hatch, not as the source of
 * truth — changing a default here changes what ships.
 */

/**
 * Bare domain, used to derive the default URLs and email addresses below.
 *
 * Still provenance.co: the app deploys to staging.provenance.co and DNS has not
 * moved yet. Changing this one value repoints every derived URL and address.
 */
export const ROOT_DOMAIN = process.env.REACT_APP_BRAND_ROOT_DOMAIN || 'lovebird.ai';

/** The consumer product name, as it appears in UI copy. */
export const BRAND_NAME = process.env.REACT_APP_BRAND_NAME || 'Lovebird';

/**
 * Lowercase, no-space form for hostnames, slugs and generated filenames.
 *
 * Deliberately a separate constant rather than lowercasing the brand name.
 * The two happen to coincide today, but the slug feeds hostnames, filenames
 * and test ids, where a display name that later gains a space, punctuation or
 * different casing would silently produce invalid values. Keep them decoupled.
 */
export const BRAND_SLUG = process.env.REACT_APP_BRAND_SLUG || 'lovebird';

/**
 * The company that owns the brand, as named in copyright notices. Separate from
 * both the product name and the ordaining organisation below.
 */
export const COMPANY_NAME =
  process.env.REACT_APP_BRAND_COMPANY_NAME || 'Sincere Corporation';

/**
 * The ordination site, as a bare hostname. Lowercase, because it is a hostname:
 * use this only where a URL or a literal address is meant.
 */
export const ORDINATION_ORG_DOMAIN =
  process.env.REACT_APP_BRAND_ORDINATION_ORG_DOMAIN || 'lovebird.org';

/**
 * The ordination site as it is NAMED in prose — "a certified minister of
 * Lovebird.org". Capitalised, and deliberately NOT derived from
 * ORDINATION_ORG_DOMAIN: the two coincide as strings but play different roles,
 * and a sentence should not render a hostname where it means a proper noun.
 */
export const ORDINATION_ORG_NAME =
  process.env.REACT_APP_BRAND_ORDINATION_ORG_NAME || 'Lovebird.org';

/**
 * The ordaining organisation: a California nonprofit religious corporation,
 * separate from both the product and the site above.
 *
 * Re-introduced deliberately. The rebrand briefly collapsed this into
 * "lovebird.org", but the nonprofit is a distinct legal entity and the
 * ordination consent copy has to name it — and it is the name printed on the
 * generated ordination documents (`cbl-backend/public/templates/pdf/`, which
 * say "The Provenance Center", and the diploma artwork, which says "The
 * Provenance Center for Ordination").
 *
 * Never fold this into BRAND_NAME or ORDINATION_ORG_NAME.
 */
export const ORDINATION_LEGAL_ENTITY_NAME =
  process.env.REACT_APP_BRAND_ORDINATION_LEGAL_ENTITY_NAME ||
  'The Provenance Center for Ordination';

/**
 * "Officiating for Beginners" training page.
 *
 * NOT derived from ORDINATION_ORG_DOMAIN: the page is still hosted on the old
 * PCO site. Move it to lovebird.org before changing this, or the link 404s.
 */
export const ORDINATION_TRAINING_URL =
  process.env.REACT_APP_ORDINATION_TRAINING_URL ||
  'https://www.thepco.org/wedding-officiant-training';

/** Marketing site root, no trailing slash. */
export const MARKETING_URL =
  process.env.REACT_APP_MARKETING_URL || `https://www.${ROOT_DOMAIN}`;

/** Authenticated app root, no trailing slash. */
export const APP_URL = process.env.REACT_APP_APP_URL || `https://app.${ROOT_DOMAIN}`;

/**
 * Help centre root, no trailing slash.
 *
 * The Help Scout Docs site. NOT derived from the brand name: the hostname is
 * registered on Help Scout's side and does not necessarily follow the slug.
 */
export const HELP_URL =
  process.env.REACT_APP_HELP_URL || 'https://help.lovebird.com/collection/1123-ceremony-officiating';

/** Marketing pages linked from inside the app. */
export const MARKETING_LINKS = {
  home: MARKETING_URL,
  termsOfService: `${MARKETING_URL}/terms-of-service`,
  privacyPolicy: `${MARKETING_URL}/privacy-policy`,
  legalDocuments: `${MARKETING_URL}/legal-documents`,
  gifting: `${MARKETING_URL}/gifting`,
  book: `${MARKETING_URL}/book`,
  blog: `${MARKETING_URL}/blog-home`,
};

/** Support addresses shown to users. */
export const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || `support@${ROOT_DOMAIN}`;

export const SPEECHWRITING_EMAIL =
  process.env.REACT_APP_SPEECHWRITING_EMAIL || `speechwriting@${ROOT_DOMAIN}`;

/** Social handle, used to build the profile URLs below. */
export const SOCIAL_HANDLE = process.env.REACT_APP_SOCIAL_HANDLE || 'ourprovenance';

export const SOCIAL_LINKS = {
  linkedin: `https://www.linkedin.com/company/${SOCIAL_HANDLE}/`,
  tiktok: `https://tiktok.com/@${SOCIAL_HANDLE}`,
  pinterest: `https://pinterest.com/${SOCIAL_HANDLE}`,
  facebook: `https://www.facebook.com/${SOCIAL_HANDLE}/`,
  instagram: `https://www.instagram.com/${SOCIAL_HANDLE}/`,
};

/**
 * Help-centre article links. The slugs themselves contain the brand name, so
 * they are overridable as whole URLs rather than assembled from HELP_URL.
 */
export const HELP_ARTICLES = {
  proForPlanners:
    process.env.REACT_APP_HELP_ARTICLE_PRO_PLANNERS ||
    `${HELP_URL}/articles/63-how-does-provenance-pro-for-wedding-planners-work`,
  proHowItWorks:
    process.env.REACT_APP_HELP_ARTICLE_PRO ||
    `${HELP_URL}/articles/63-how-does-provenance-pro-work`,
  referrals:
    process.env.REACT_APP_HELP_ARTICLE_REFERRALS ||
    `${HELP_URL}/articles/57-how-do-referrals-work-on-provenance`,
};

/**
 * Calendly booking link for the free public-speaking consultation.
 *
 * The slug is registered on Calendly's side and contains the brand name, so it
 * is overridable as a whole URL. Renaming it here without renaming the event in
 * Calendly gives a 404.
 */
export const CONSULTATION_URL =
  process.env.REACT_APP_CONSULTATION_URL ||
  'https://calendly.com/d/xzx-cvk-5yn/provenance-free-public-speaking-consultation';

/** Prefix for `document.title`, e.g. `${PAGE_TITLE_PREFIX} – Billing Settings`. */
export const PAGE_TITLE_PREFIX = `${BRAND_NAME} App`;

/** Convenience helper for page titles. */
export const pageTitle = (page: string): string => `${PAGE_TITLE_PREFIX} – ${page}`;

const brand = {
  ROOT_DOMAIN,
  BRAND_NAME,
  BRAND_SLUG,
  COMPANY_NAME,
  ORDINATION_ORG_DOMAIN,
  ORDINATION_LEGAL_ENTITY_NAME,
  ORDINATION_ORG_NAME,
  ORDINATION_TRAINING_URL,
  MARKETING_URL,
  APP_URL,
  HELP_URL,
  MARKETING_LINKS,
  SUPPORT_EMAIL,
  SPEECHWRITING_EMAIL,
  SOCIAL_HANDLE,
  SOCIAL_LINKS,
  HELP_ARTICLES,
  CONSULTATION_URL,
  PAGE_TITLE_PREFIX,
  pageTitle,
};

export default brand;
